/**
 * Every string a player ever reads or hears, in French.
 *
 * The server is deliberately language-free: it sends role ids, slot
 * references and narration *keys*. This module is the only place that turns
 * them into words, which means adding a locale later is a matter of copying
 * this file and the two data files beside it - what the narrator says, and
 * who says it - then picking one at startup. No rules code has to move.
 *
 * Keep the copy short. The whole UI is designed to fit a phone screen without
 * scrolling, and half of it is read by someone who has just opened their eyes.
 */

import type { ErrorCode, RoundOutcome } from "../../shared/protocol.js";
import type { RoleId } from "../../shared/roles.js";
import script from "./narration.fr.json";
import cast from "./voices.fr.json";

/* -------------------------------------------------------------------------- */
/* Roles                                                                      */
/* -------------------------------------------------------------------------- */

/** Names as printed on the French edition of the cards. */
export const ROLE_NAMES: Record<RoleId, string> = {
  werewolf: "Loup-Garou",
  minion: "Sbire",
  mason: "Franc-Maçon",
  villager: "Villageois",
  seer: "Voyante",
  robber: "Voleur",
  troublemaker: "Noiseuse",
  drunk: "Soûlard",
  insomniac: "Insomniaque",
  hunter: "Chasseur",
  tanner: "Tanneur",
};

/** Plural form, used by the deck builder. */
export const ROLE_NAMES_PLURAL: Record<RoleId, string> = {
  werewolf: "Loups-Garous",
  minion: "Sbires",
  mason: "Francs-Maçons",
  villager: "Villageois",
  seer: "Voyantes",
  robber: "Voleurs",
  troublemaker: "Noiseuses",
  drunk: "Soûlards",
  insomniac: "Insomniaques",
  hunter: "Chasseurs",
  tanner: "Tanneurs",
};

/** One line shown under the card during the reveal. */
export const ROLE_TAGLINES: Record<RoleId, string> = {
  werewolf: "Vous dévorez le village. Ne vous faites pas démasquer.",
  minion: "Vous servez les loups-garous : vous les voyez, ils ne vous voient pas.",
  mason: "Vous reconnaissez l'autre franc-maçon. À vous deux, vous êtes sûrs.",
  villager: "Aucun pouvoir. Votre seule arme, c'est la parole.",
  seer: "Vous voyez une carte cachée pendant la nuit.",
  robber: "Vous volez la carte d'un joueur et devenez son rôle.",
  troublemaker: "Vous échangez les cartes de deux joueurs, à l'aveugle.",
  drunk: "Vous prenez une carte du centre sans jamais la regarder.",
  insomniac: "À la fin de la nuit, vous revoyez votre propre carte.",
  hunter: "Si vous mourez, celui que vous avez désigné meurt avec vous.",
  tanner: "Vous ne gagnez qu'en mourant. Faites-vous éliminer.",
};

/** Instruction shown to the acting player at the top of their night turn. */
export const ROLE_NIGHT_PROMPTS: Record<RoleId, string> = {
  werewolf: "Repérez vos complices.",
  minion: "Repérez les loups-garous. Ils ne sauront pas qui vous êtes.",
  mason: "Repérez l'autre franc-maçon.",
  villager: "Vous dormez.",
  seer: "Regardez la carte d'un joueur, ou deux cartes du centre.",
  robber: "Choisissez un joueur : vous prenez sa carte, il reçoit la vôtre.",
  troublemaker: "Échangez les cartes de deux autres joueurs, sans les voir.",
  drunk: "Choisissez une carte du centre : vous la prenez sans la regarder.",
  insomniac: "Voici votre carte, telle qu'elle est au bout de la nuit.",
  hunter: "Vous dormez.",
  tanner: "Vous dormez.",
};

/* -------------------------------------------------------------------------- */
/* Narration (spoken by the host device)                                      */
/* -------------------------------------------------------------------------- */

type NarrationParams = Record<string, string | number>;

/**
 * Every sentence the narrator can say, by clip. Most clips are named exactly
 * as the server emits its cues: `phase.*`, `wake.<roleId>`, `sleep.<roleId>`,
 * `outcome.*`. A role that never wakes needs no entry.
 *
 * The words live in a data file rather than here because nothing reads them
 * out at run time: each one is a recording, per voice, under `public/voices/`,
 * and the renderer that makes those reads the same file. Changing a sentence
 * therefore means recording it again - `docs/voices.md` says how, and
 * `npm test` fails until it is done.
 */
const SCRIPT: Record<string, string> = script;

/**
 * Cues that end by telling the table how long it has. Their sentence is
 * followed by a second clip, `<prefix>.<seconds>`: the script holds one for
 * every duration the lobby's steppers can reach.
 */
const TIMED: Record<string, string> = {
  "phase.roleReveal": "reveal",
  "phase.day": "debate",
};

/**
 * The clips to play, in order, for one cue from the server. An unknown key
 * yields none (it never throws), and a duration nobody recorded is left out.
 */
export function narrationClips(key: string, params: NarrationParams = {}): string[] {
  const prefix = TIMED[key];
  const clips = prefix ? [key, `${prefix}.${params.seconds}`] : [key];
  return clips.filter((clip) => typeof SCRIPT[clip] === "string");
}

/** Every clip but the durations: what a narrator phone fetches before a round. */
export const NARRATION_STAPLES: readonly string[] = Object.keys(SCRIPT).filter(
  (clip) => !Object.values(TIMED).some((prefix) => clip.startsWith(`${prefix}.`)),
);

export interface Voice {
  /** Folder of recordings under `/voices/`, and what is remembered between visits. */
  id: string;
  /** Shown in the lobby's voice picker. */
  name: string;
}

/** The narrators that ship with the app, in the order the picker lists them. */
export const VOICES: readonly Voice[] = cast;

/** Used until the host picks one, and whenever a remembered voice is gone. */
export const DEFAULT_VOICE = VOICES[0]?.id ?? "";

export function isVoice(id: string | null): id is string {
  return VOICES.some((voice) => voice.id === id);
}

/* -------------------------------------------------------------------------- */
/* Interface                                                                  */
/* -------------------------------------------------------------------------- */

/** Headline on the reveal screen, one per possible winner. */
const OUTCOME_TITLES: Record<RoundOutcome, string> = {
  village: "Le village gagne !",
  werewolf: "Les loups-garous gagnent !",
  tanner: "Le tanneur gagne !",
  tanner_village: "Le tanneur et le village gagnent !",
  nobody: "Personne ne gagne",
};

/** The line under it: why it ended that way. */
const OUTCOME_DETAILS: Record<RoundOutcome, string> = {
  village: "Un loup-garou a été éliminé — ou le village a su n'accuser personne.",
  werewolf: "Aucun loup-garou n'a été éliminé.",
  tanner: "Le tanneur voulait mourir, et il est mort. Tous les autres perdent.",
  tanner_village: "Le tanneur est mort, et un loup-garou est tombé avec lui.",
  nobody: "Il n'y avait aucun loup-garou, et un innocent a été éliminé.",
};

export const UI = {
  appName: "WebWolf",
  tagline: "Une nuit chez les loups-garous, sans cartes ni déplacements.",

  // Home ------------------------------------------------------------------
  nicknameLabel: "Votre pseudo",
  nicknamePlaceholder: "Camille",
  createRoom: "Créer une partie",
  joinRoom: "Rejoindre",
  codeLabel: "Code du salon",
  codePlaceholder: "ABCD",
  connecting: "Connexion…",
  reconnecting: "Reconnexion…",
  offline: "Hors ligne — nouvelle tentative…",

  // Lobby -----------------------------------------------------------------
  lobbyTitle: "Salon",
  shareCode: "Donnez ce code à vos amis",
  playersTitle: (n: number) => `Joueurs (${n})`,
  hostBadge: "Animateur",
  youBadge: "Vous",
  disconnectedBadge: "Déconnecté",
  removePlayer: "Retirer",
  deckTitle: "Composition",
  deckCount: (actual: number, required: number) => `${actual} / ${required} cartes`,
  deckHint: (players: number) =>
    `${players} joueurs + 3 cartes au centre — le deck doit contenir exactement ${players + 3} cartes.`,
  settingsTitle: "Réglages de la manche",
  settingRoleReveal: "Découverte du rôle",
  settingNightStep: "Durée d'un tour de nuit",
  settingDiscussion: "Discussion",
  settingVote: "Vote",
  settingNarration: "Narration vocale",
  narrationHint: "Cet appareil lit les phases à voix haute. Posez-le au centre de la table.",
  settingVoice: "Voix",
  testVoice: "Tester la voix",
  startGame: "Lancer la partie",
  waitingForHost: "En attente de l'animateur…",
  leaveRoom: "Quitter",

  // Shared device ----------------------------------------------------------
  deviceTitle: "Cet appareil",
  deviceHint:
    "Deux joueurs peuvent partager ce téléphone : chacun a sa carte, son tour et son vote, et l'écran passe de l'un à l'autre.",
  deviceFull: "Cet appareil accueille déjà deux joueurs.",
  roomFullHint: "La table est complète.",
  deviceBadge: (group: number) => `Appareil ${group}`,
  addPlayer: "Ajouter un 2e joueur",
  addPlayerTitle: "Deuxième joueur",
  addPlayerIntro:
    "Ce joueur partage votre téléphone. Il reçoit sa propre carte et vote de son côté ; vous vous passerez l'écran au bon moment.",
  addPlayerLabel: "Pseudo du 2e joueur",
  addPlayerConfirm: "Ajouter à la table",
  removeSeat: "Retirer de la table",
  cancel: "Annuler",
  /** Ends one player's turn with the screen and hands the phone back. */
  handoverDone: "Terminé — rendre le téléphone",

  // Role reveal -----------------------------------------------------------
  revealTitle: "Votre carte",
  revealTitleFor: (nickname: string) => `Carte de ${nickname}`,
  revealWarning: "Mémorisez-la : vous ne pourrez plus la revoir.",
  revealAck: "C'est mémorisé",
  revealWaiting: (ready: number, total: number) => `${ready} / ${total} prêts`,
  /** Gate shown before any card is drawn, so nobody uncovers someone else's. */
  revealGateSolo: "Votre carte est prête.",
  revealGateShared: (nickname: string) => `Passez le téléphone à ${nickname}.`,
  revealGateCaution: "Personne d'autre ne doit voir l'écran.",
  revealGateButtonSolo: "Voir ma carte",
  /** First line of the shared-phone button; `revealGateButtonSolo` is the second. */
  revealGateButton: (nickname: string) => `Je suis ${nickname}`,
  revealSeatDone: "carte mémorisée",
  revealSeatWaiting: "n'a pas encore regardé",

  // Night -----------------------------------------------------------------
  nightTitle: "La nuit",
  nightKeepEyesClosed: "Gardez les yeux fermés.",
  /** The pause that opens the night, before the first role is called. */
  nightSettle: "Posez le téléphone sur la table et fermez les yeux.",
  nightSettleStep: "Le premier rôle va être appelé.",
  nightStep: (step: number, total: number, role: string) => `Tour ${step}/${total} — ${role}`,
  nightFellows: "Vos complices :",
  fellowNote: "Complice",
  swappedNote: "Échangée",
  nightAlone: "Vous êtes le seul loup-garou. Vous pouvez regarder une carte du centre.",
  nightMasonAlone: "Vous êtes le seul franc-maçon : l'autre carte est au centre.",
  nightMinionAlone: "Aucun loup-garou à la table : ils sont tous au centre.",
  nightSkip: "Passer",
  /** Host only: abandons the round from the night. */
  stopRound: "Arrêter la manche",
  stopConfirmTitle: "Arrêter la manche ?",
  stopConfirmBody:
    "La nuit s'arrête net : les cartes de cette manche sont perdues et toute la table revient au salon.",
  stopConfirmCaution: "Prévenez les joueurs à voix haute : ils ont les yeux fermés.",
  stopConfirmYes: "Oui, arrêter",
  stopConfirmNo: "Continuer la nuit",
  nightSkipped: "Vous avez passé votre tour.",
  nightNothingToDo: "Rien à faire ce tour-ci. Regardez bien, puis refermez les yeux.",
  nightSwapDone: "Échange effectué.",
  nightRobbedInto: "Voici votre nouvelle carte. Mémorisez-la.",
  nightYouSee: "Mémorisez cette carte, puis refermez les yeux.",
  nightCloseAgain: "Refermez les yeux à la fin du tour.",

  // Table ------------------------------------------------------------------
  centerCard: (index: number) => `Centre ${index + 1}`,

  // Day --------------------------------------------------------------------
  dayTitle: "Le jour se lève",
  dayInstruction: "Débattez. Qui est le loup-garou ?",
  endDiscussion: "Passer au vote",

  // Vote -------------------------------------------------------------------
  voteTitle: "Le vote",
  voteInstruction: "Désignez le joueur à éliminer.",
  voteProgress: (voted: number, total: number) => `${voted} / ${total} ont voté`,
  voteYours: (nickname: string) => `Votre vote : ${nickname}`,
  voteChangeable: "Vous pouvez encore changer d'avis.",
  voteGateInstruction: "Chacun vote de son côté, sans montrer son choix à l'autre.",
  voteGateButton: (nickname: string) => `${nickname} — voter`,
  voteSeatDone: "a voté",
  voteSeatWaiting: "n'a pas encore voté",
  voteTitleFor: (nickname: string) => `Vote de ${nickname}`,

  // Reveal -----------------------------------------------------------------
  revealResultTitle: "Résultat",
  outcomeTitle: (outcome: RoundOutcome) => OUTCOME_TITLES[outcome],
  outcomeDetail: (outcome: RoundOutcome) => OUTCOME_DETAILS[outcome],
  nobodyDied: "Personne n'a été éliminé : chaque joueur a reçu exactement une voix.",
  eliminatedLabel: "Éliminé",
  huntedLabel: "Abattu",
  huntedNote: "Le chasseur a emporté sa cible avec lui.",
  votesReceived: (n: number) => (n === 1 ? "1 voix" : `${n} voix`),
  dealtToFinal: "→",
  centerTitle: "Cartes du centre",
  playAgain: "Rejouer",
} as const;

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

const ERROR_MESSAGES: Record<ErrorCode, string> = {
  bad_protocol: "Cette page n'est plus à jour. Rechargez-la.",
  bad_request: "Requête invalide.",
  room_not_found: "Aucun salon avec ce code.",
  room_full: "Ce salon est complet.",
  game_in_progress: "Une manche est déjà en cours dans ce salon.",
  not_host: "Seul l'animateur peut faire ça.",
  not_in_room: "Vous n'êtes plus dans le salon.",
  invalid_deck: "La composition n'est pas jouable.",
  device_full: "Cet appareil accueille déjà deux joueurs.",
  invalid_action: "Action impossible pour l'instant.",
  kicked: "Vous avez été retiré du salon.",
  internal: "Erreur interne. Réessayez.",
};

export function errorMessage(code: ErrorCode): string {
  return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.internal;
}

/** Why the current deck cannot be played, phrased for the host. */
export function deckProblem(
  reason: string | undefined,
  detail: Record<string, string | number> | undefined,
): string {
  switch (reason) {
    case "too_few_players":
      return `Il faut au moins ${detail?.min ?? 3} joueurs.`;
    case "wrong_size":
      return `Il faut exactement ${detail?.required ?? "?"} cartes.`;
    case "too_many_copies":
      return "Trop d'exemplaires d'une carte.";
    case "no_werewolf":
      return "Ajoutez au moins un Loup-Garou.";
    default:
      return "Composition invalide.";
  }
}

/** mm:ss for on-screen countdowns. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}:${String(seconds).padStart(2, "0")}` : `${seconds}`;
}

/** "5 min", "45 s" - compact form for the lobby's settings steppers. */
export function formatDuration(seconds: number): string {
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} min`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
  return `${seconds} s`;
}
